import type { Chat, ChatMessage } from '../types';
import { useAppStore } from '../stores/useAppStore';
import { chatMeta, chats as chatStore } from '../storage/db';
import { summarizeChatTitle } from '../pipeline/chatTurn';

function shallowEqualChat(a: Chat, b: Chat): boolean {
  return (
    a.id === b.id &&
    a.universeId === b.universeId &&
    a.title === b.title &&
    a.createdAt === b.createdAt &&
    a.lastUsedAt === b.lastUsedAt &&
    a.messageCount === b.messageCount
  );
}

let lastSnapshot: Record<string, Chat> = {};

/** Mirror chat metadata to IDB whenever the zustand chats map changes. */
export function startChatMetaMirror() {
  lastSnapshot = useAppStore.getState().chats;
  useAppStore.subscribe((state, prev) => {
    if (state.chats === prev.chats) return;
    const next = state.chats;
    const writes: Promise<void>[] = [];
    for (const id of Object.keys(next)) {
      const cur = next[id];
      const old = lastSnapshot[id];
      if (!old || !shallowEqualChat(old, cur)) {
        writes.push(
          chatMeta.set(id, cur).catch((e) => {
            console.warn('chatMeta mirror set failed', id, e);
          })
        );
      }
    }
    for (const id of Object.keys(lastSnapshot)) {
      if (!next[id]) {
        writes.push(
          chatMeta.del(id).catch((e) => {
            console.warn('chatMeta mirror del failed', id, e);
          })
        );
      }
    }
    lastSnapshot = next;
    void Promise.all(writes);
  });
}

function deriveTitleFromMessages(messages: ChatMessage[]): string {
  for (const m of messages) {
    if (m.role !== 'user') continue;
    for (const b of m.content) {
      if (b.type === 'text' && b.text.trim()) {
        return b.text.slice(0, 60).replace(/\s+/g, ' ').trim();
      }
    }
  }
  return 'Recovered chat';
}

function countUserTurns(messages: ChatMessage[]): number {
  let n = 0;
  for (const m of messages) {
    if (m.role !== 'user') continue;
    if (m.content.some((b) => b.type === 'text')) n++;
  }
  return n;
}

function latestTimestamp(messages: ChatMessage[], fallback: number): number {
  let best = 0;
  for (const m of messages) if (m.at > best) best = m.at;
  return best || fallback;
}

/**
 * On boot, repair the chats map from IDB:
 *   1. Add any chat-meta records that exist in IDB but not in the zustand map
 *      (localStorage was wiped but IDB survived).
 *   2. Add any messages-only entries that have no metadata anywhere
 *      (older chats predating the mirror, or partial wipes).
 */
export async function recoverChatsFromIdb(): Promise<{
  addedFromMeta: number;
  addedFromMessages: number;
}> {
  const state = useAppStore.getState();
  const current = state.chats;

  let metaList: Chat[] = [];
  try {
    metaList = await chatMeta.all();
  } catch (e) {
    console.warn('chatMeta.all failed', e);
  }

  const recovered: Record<string, Chat> = {};
  for (const m of metaList) {
    if (!m || typeof m.id !== 'string') continue;
    if (!current[m.id]) recovered[m.id] = m;
  }

  let messageKeys: IDBValidKey[] = [];
  try {
    messageKeys = await chatStore.keys();
  } catch (e) {
    console.warn('chats.keys failed', e);
  }

  const knownIds = new Set([
    ...Object.keys(current),
    ...Object.keys(recovered),
  ]);
  let addedFromMessages = 0;
  for (const k of messageKeys) {
    const id = String(k);
    if (knownIds.has(id)) continue;
    try {
      const msgs = (await chatStore.get(id)) ?? [];
      if (msgs.length === 0) continue;
      const fallback = Date.now();
      const reconstructed: Chat = {
        id,
        universeId: '__orphan__',
        title: deriveTitleFromMessages(msgs),
        createdAt: latestTimestamp(msgs, fallback),
        lastUsedAt: latestTimestamp(msgs, fallback),
        messageCount: countUserTurns(msgs),
      };
      recovered[id] = reconstructed;
      addedFromMessages++;
    } catch (e) {
      console.warn('recoverChatsFromIdb: failed to read messages', id, e);
    }
  }

  const addedFromMeta = Object.keys(recovered).length - addedFromMessages;
  if (Object.keys(recovered).length > 0) {
    useAppStore.setState((s) => ({ chats: { ...s.chats, ...recovered } }));
    console.info(
      `[chat-recovery] restored ${addedFromMeta} from meta, ${addedFromMessages} from messages`
    );
  }
  return { addedFromMeta, addedFromMessages };
}

/**
 * A chat title is considered auto-generated (and therefore safe to overwrite
 * with a Haiku-derived summary) if it matches one of the deterministic
 * fallback shapes we ourselves produce: "New chat", "Recovered chat", or the
 * 60-char slice of the first user-text message.
 */
function isAutoGeneratedTitle(chat: Chat, messages: ChatMessage[]): boolean {
  if (chat.title === 'New chat' || chat.title === 'Recovered chat') return true;
  if (chat.title === deriveTitleFromMessages(messages)) return true;
  return false;
}

/**
 * Walk through every chat in the store and replace any auto-generated title
 * with a Haiku-derived topic-style summary. Sequential and best-effort; one
 * Haiku call per stale chat. Failures leave the existing title in place.
 */
export async function backfillChatTitles(): Promise<void> {
  // Snapshot the chat list. We deliberately re-read store state inside the
  // loop in case other writes land while we're working.
  const initial = useAppStore.getState().chats;
  const ids = Object.keys(initial);
  let updated = 0;
  for (const id of ids) {
    const chat = useAppStore.getState().chats[id];
    if (!chat) continue;
    if (chat.universeId === '__orphan__') continue;
    if (chat.messageCount <= 0) continue;

    let messages: ChatMessage[] | undefined;
    try {
      messages = await chatStore.get(id);
    } catch (e) {
      console.warn('[chat-summary backfill] read failed', id, e);
      continue;
    }
    if (!messages || messages.length === 0) continue;
    if (!isAutoGeneratedTitle(chat, messages)) continue;

    const summary = await summarizeChatTitle(messages);
    if (!summary) continue;
    const fresh = useAppStore.getState().chats[id];
    if (!fresh) continue;
    if (!isAutoGeneratedTitle(fresh, messages)) continue;
    useAppStore.setState((s) => {
      const cur = s.chats[id];
      if (!cur) return {} as never;
      return {
        chats: {
          ...s.chats,
          [id]: { ...cur, title: summary },
        },
      } as never;
    });
    updated++;
  }
  if (updated > 0) {
    console.info(`[chat-summary backfill] updated ${updated} chat title(s)`);
  }
}
